import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  Row,
  Col,
} from 'reactstrap';
import { getActiveColorStyle, getButtonColor } from '../../utils/colorUtils';
import { useSendChatMessage } from '../../api/chat/service';

const Chat = ({ activeColor = "secondary", isLightTheme }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const activeColorStyle = getActiveColorStyle(activeColor);
  const buttonColor = getButtonColor(activeColor);

  // API hooks
  const sendMessageMutation = useSendChatMessage();

  // Session ID for chat
  const [sessionId] = useState(() => `chat-session-${Date.now()}`);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (newMessage.trim() === '') return;

    const messageToSend = newMessage;

    const userMessage = {
      id: Date.now(),
      text: messageToSend,
      sender: "user",
      timestamp: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setNewMessage('');
    setIsTyping(true);

    // Backend'e tüm geçmişi gönderiyoruz (CoreMessage formatı API katmanında hallediliyor)
    sendMessageMutation.mutate({ 
      messages: updatedMessages, 
      userContext: { id: 1, role: 'admin' } 
    }, {
      onSuccess: (botText) => {
        const botResponse = {
          id: Date.now() + 1,
          text: botText || "I received your message.",
          sender: "bot",
          timestamp: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
        };
        setMessages(prev => [...prev, botResponse]);
        setIsTyping(false);
      },
      onError: (error) => {
        console.error('Error sending message:', error);
        const errorResponse = {
          id: Date.now() + 2,
          text: "Sorry, I'm having trouble connecting right now. Please try again later.",
          sender: "bot",
          timestamp: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
        };
        setMessages(prev => [...prev, errorResponse]);
        setIsTyping(false);
      }
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickResponses = [
    "How to add vehicle?",
    "How to add user?",
    "I need help",
    "Technical support"
  ];

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card style={{
            height: 'calc(100vh - 150px)',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: isLightTheme ? '#ffffff' : '#1e1e2f'
          }}>
            <CardBody style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              padding: '1.5rem',
              maxWidth: '95%'
            }}>
              <div style={{
                flex: 1,
                overflowY: 'auto',
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: isLightTheme ? '#f8f9fa' : 'rgba(0,0,0,0.2)',
                borderRadius: '8px'
              }}>
                {messages.length === 0 ? (
                  <div style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.5
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>💬</div>
                    <h5>No messages yet</h5>
                    <p>Start a conversation by typing below or choosing a quick response.</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      style={{
                        marginBottom: '1rem',
                        display: 'flex',
                        justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <div style={{
                        maxWidth: '70%',
                        padding: '1rem',
                        borderRadius: '12px',
                        backgroundColor: message.sender === 'user'
                          ? buttonColor
                          : (isLightTheme ? '#e9ecef' : '#32325d'),
                        color: message.sender === 'user' ? 'white' : (isLightTheme ? '#32325d' : '#f6f9fc'),
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        position: 'relative'
                      }}>
                        {message.text}
                        <div style={{
                          fontSize: '10px',
                          opacity: 0.7,
                          marginTop: '5px',
                          textAlign: 'right'
                        }}>
                          {message.timestamp}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {isTyping && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1rem' }}>
                    <div style={{
                      padding: '0.8rem 1.2rem',
                      borderRadius: '12px',
                      backgroundColor: isLightTheme ? '#e9ecef' : '#32325d',
                      color: isLightTheme ? '#32325d' : '#f6f9fc'
                    }}>
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div style={{ marginBottom: '1rem', display: 'flex', gap: '10px', flexWrap: 'wrap', }}>
                {quickResponses.map((response, index) => (
                  <Button
                    key={index}
                    size="sm"
                    color="primary"
                    outline
                    onClick={() => setNewMessage(response)}
                    style={{ borderRadius: '20px', textTransform: 'none' }}
                  >
                    {response}
                  </Button>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <Input
                  type="textarea"
                  placeholder="Type your message here..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={{
                    resize: 'none',
                    borderRadius: '8px',
                    backgroundColor: isLightTheme ? '#ffffff' : '#1e1e2f',
                    color: isLightTheme ? '#32325d' : 'white',
                    borderColor: isLightTheme ? '#dee2e6' : '#32325d'
                  }}
                  rows={2}
                />
                <Button
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sendMessageMutation.isPending}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...activeColorStyle
                  }}
                >
                  <i className="tim-icons icon-send" />
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <style>{`
        .typing-indicator {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .typing-indicator span {
          width: 6px;
          height: 6px;
          background-color: currentColor;
          border-radius: 50%;
          display: inline-block;
          animation: bounce 1.4s infinite ease-in-out both;
        }
        .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
        .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1.0); }
        }
      `}</style>
    </div>
  );
};

export default Chat;
